import { IconButton } from "@itk/mui-components";
import { Grid, InputAdornment, Skeleton, Stack, TextField } from "@mui/material";
import { ComponentType, useEffect, useMemo, useState } from "react";
import { DekoxIcon, ViewMode } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { ITEM_CARD_IMAGE_HEIGHT } from "../../add-edit-configuration-dialog-types";
import { ItemCard } from "../item-card";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { addEditConfigurationDialogActions } from "../../add-edit-configuration-dialog-store";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";

const mapStateToProps = (state: RootState) => ({
  systemPackagesState: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.systemPackagesState,
  formData: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.formData
});

const ownActions = {
  getSystemPackagesRequest: addEditConfigurationDialogActions.getSystemPackagesRequest,
  setFormData: addEditConfigurationDialogActions.setFormData
};

type OwnProps = { onSave?: () => void };
type Props = typeof ownActions & ReturnType<typeof mapStateToProps> & OwnProps;

const SelectSystemBase = (props: Props) => {
  const { getSystemPackagesRequest, systemPackagesState, formData, setFormData, onSave } = props;

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  useEffect(() => {
    getSystemPackagesRequest();
  }, []);

  const [searchText, setSearchText] = useState("");

  const systemPackages = useMemo(() => {
    if (systemPackagesState.loading === "has-loaded") {
      return systemPackagesState.data
        .sort((x, y) => x.sortOrder - y.sortOrder)
        .filter(x => {
          // description replaces name on the regenerated Model_SystemPackageGet
          return x.description.toLowerCase().includes(searchText);
        });
    }
    return [];
  }, [searchText, systemPackagesState.loading]);

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={2}>
        <TextField
          autoFocus
          fullWidth
          value={searchText}
          onChange={e => {
            setSearchText(e.target.value);
          }}
          placeholder={genericTranslationsT.search}
          autoComplete="search"
          sx={{
            "& .MuiInputBase-input": {
              padding: 0
            },
            "& .MuiInputBase-root .MuiInputBase-input": {
              height: 40
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <DekoxIcon icon="search" color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchText && (
                    <IconButton k={genericTranslationsK.dynamicValue} onClick={() => setSearchText("")}>
                      <DekoxIcon fontSize="small" icon="close" />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }
          }}
        />
        <ViewMode value={formData?.viewMode || "FULL"} onChange={value => setFormData({ viewMode: value })} />
      </Stack>
      <Grid container spacing={2}>
        {systemPackagesState.loading === "is-loading" && (
          <>
            {Array(4)
              .fill(undefined)
              .map((_, index) => (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Skeleton height={formData.viewMode === "FULL" ? ITEM_CARD_IMAGE_HEIGHT : 62} />
                </Grid>
              ))}
          </>
        )}
        {systemPackagesState.loading === "has-loaded" && systemPackages.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Stack alignItems="center" py={4}>
              {configuratorPageT.noSystemsFound}
            </Stack>
          </Grid>
        )}
        {systemPackagesState.loading === "has-loaded" && (
          <>
            {systemPackages.map((systemPackage, index) => {
              return (
                <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <ItemCard
                    id={systemPackage.systemPackageId}
                    name={systemPackage.description}
                    type={configuratorPageT.system}
                    isSelected={formData?.productsFilter?.systemPackageId === systemPackage.systemPackageId}
                    onSelect={() => {
                      setFormData({ productsFilter: { systemPackageId: systemPackage.systemPackageId } });
                    }}
                    onDoubleClick={onSave}
                    isMinimal={formData.viewMode === "MINIMAL"}
                  />
                </Grid>
              );
            })}
          </>
        )}
      </Grid>
    </Stack>
  );
};

export const SelectSystem = connect(mapStateToProps, ownActions)(SelectSystemBase) as unknown as ComponentType<OwnProps>;
